import { useEffect, useState } from "react";
import { BellRing } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApi, errMsg } from "@/lib/api";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;

function toKey(b64) {
  const s = (b64 + "=".repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
}

export function PushCard() {
  const api = useApi();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (supported()) navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then(setSub).catch(() => {});
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      if ((await Notification.requestPermission()) !== "granted") throw new Error("Notifications were not allowed on this device");
      const reg = await navigator.serviceWorker.ready;
      const { key } = await api("get", "/push/public-key");
      const s = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(key) }));
      await api("post", "/push/subscribe", s.toJSON());
      setSub(s);
      toast.success("Phone notifications on");
    } catch (e) { toast.error(e.message && !e.response ? e.message : errMsg(e)); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { await api("post", "/push/unsubscribe", sub.toJSON()); await sub.unsubscribe(); setSub(null); toast.success("Phone notifications off"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const test = async () => {
    try { await api("post", "/push/test"); toast.success("Test sent"); } catch (e) { toast.error(errMsg(e)); }
  };

  let body;
  if (isIos() && !standalone()) body = <p className="text-sm text-slate-500" data-testid="push-ios-hint">On iPhone: open this site in Safari, tap Share → Add to Home Screen, then open WindWorkHub from the home screen and come back here.</p>;
  else if (!supported()) body = <p className="text-sm text-slate-500" data-testid="push-unsupported">This browser doesn't support push notifications.</p>;
  else if (sub) body = (
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" onClick={test} data-testid="push-test-button">Send test</Button>
      <Button variant="outline" onClick={disable} disabled={busy} data-testid="push-disable-button">Turn off on this device</Button>
    </div>
  );
  else body = <Button onClick={enable} disabled={busy} data-testid="push-enable-button"><BellRing size={14} />Turn on phone notifications</Button>;

  return (
    <div className="panel p-6 max-w-lg space-y-3 mt-6" data-testid="admin-push-card">
      <p className="font-semibold">Phone notifications {sub && <span className="text-emerald-600 text-sm font-normal" data-testid="push-status-on">· on for this device</span>}</p>
      <p className="text-sm text-slate-600">New members, new jobs, content reports and contact messages.</p>
      {body}
    </div>
  );
}
